<template>
  <div class="card shadow-sm p-3">
    <div class="d-flex align-items-center gap-2 mb-3">
      <span class="material-icons text-primary">description</span>
      <span class="fw-bold">Validación 002 — Planilla {{ numero }}</span>
    </div>

    <button
      class="btn btn-primary btn-sm d-flex align-items-center gap-1"
      @click="validar002"
      :disabled="procesando"
    >
      <span v-if="procesando" class="spinner-border spinner-border-sm"></span>
      <span v-else class="material-icons" style="font-size:16px">verified</span>
      {{ procesando ? 'Validando...' : 'Validar Formulario 002' }}
    </button>

    <!-- Error archivo -->
    <div v-if="errorMensaje" class="alert alert-danger small mt-3">
      <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">error</span>
      {{ errorMensaje }}
    </div>

    <!-- Resultado -->
    <div v-if="resultado" class="mt-3">
      <hr />

      <!-- Alerta resumen -->
      <div class="alert py-2 small" :class="alertaClase">
        <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">
          {{ todoCoincide ? 'check_circle' : 'warning' }}
        </span>
        {{ todoCoincide
            ? 'Todos los datos del formulario 002 coinciden con la base de datos.'
            : 'Se encontraron diferencias entre el formulario 002 y la base de datos.' }}
      </div>

      <!-- Tabla comparación — misma estructura que CVpdfValidador -->
      <table class="table table-sm table-bordered small">
        <thead class="table-dark">
          <tr>
            <th>Campo</th>
            <th>Base de datos</th>
            <th>PDF</th>
            <th style="width:50px" class="text-center">OK</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="fila in filasComparacion" :key="fila.campo">
            <td class="fw-bold">{{ fila.campo }}</td>
            <td>{{ fila.bd ?? '-' }}</td>
            <td>{{ fila.pdf ?? '-' }}</td>
            <td class="text-center">
              <span v-if="fila.coincide === true"  class="badge bg-success">✓</span>
              <span v-else-if="fila.coincide === false" class="badge bg-danger">✗</span>
              <span v-else class="text-muted small">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Diagnósticos — idéntico al bloque de CVpdfValidador -->
      <p class="fw-bold small mb-2 mt-3">Diagnósticos CIE-10</p>

      <div class="mb-1 small">
        <span class="fw-bold text-success me-1">✓ Coinciden:</span>
        <template v-if="resultado.diagnosticosCoinciden?.length">
          <span v-for="d in resultado.diagnosticosCoinciden" :key="d"
                class="badge bg-success me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <div class="mb-1 small">
        <span class="fw-bold text-warning me-1">⚠ En BD, no en PDF:</span>
        <template v-if="resultado.diagnosticosNoPdf?.length">
          <span v-for="d in resultado.diagnosticosNoPdf" :key="d"
                class="badge bg-warning text-dark me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <div class="mb-1 small">
        <span class="fw-bold text-info me-1">⚠ En PDF, no en BD:</span>
        <template v-if="resultado.diagnosticosNoBd?.length">
          <span v-for="d in resultado.diagnosticosNoBd" :key="d"
                class="badge bg-info text-dark me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <!-- Diagnósticos del PDF con descripción y tipo -->
      <div v-if="resultado.diagnosticosPdf?.length" class="mt-3">
        <p class="fw-bold small mb-2">Detalle diagnósticos en formulario 002</p>
        <table class="table table-sm table-bordered small">
          <thead class="table-secondary">
            <tr>
              <th>Descripción</th>
              <th style="width:60px" class="text-center">CIE</th>
              <th style="width:48px" class="text-center">PRE</th>
              <th style="width:48px" class="text-center">DEF</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(d, i) in resultado.diagnosticosPdf" :key="i"
                :class="cieBdSet.has(d.cie) ? 'table-success' : ''">
              <td>{{ d.descripcion }}</td>
              <td class="text-center fw-bold">{{ d.cie }}</td>
              <td class="text-center">
                <span v-if="d.esPreliminar"
                      class="material-icons text-warning" style="font-size:14px">check</span>
              </td>
              <td class="text-center">
                <span v-if="d.esDefinitivo"
                      class="material-icons text-success" style="font-size:14px">check</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Datos del médico -->
      <div class="mt-3 small border-top pt-2">
        <p class="fw-bold mb-1">Profesional responsable (formulario 002)</p>
        <div class="row g-1">
          <div class="col-7">
            <span class="text-muted fw-bold">Nombre: </span>
            {{ resultado.nombreMedicoPdf ?? '-' }}
          </div>
          <div class="col-5">
            <span class="text-muted fw-bold">Cédula: </span>
            {{ resultado.cedulaMedicoPdf ?? '-' }}
          </div>
          <div class="col-6">
            <span class="text-muted fw-bold">Fecha firma: </span>
            {{ resultado.fechaMedicoPdf ?? '-' }}
          </div>
        </div>
      </div>

      <!-- Texto PDF colapsable — idéntico a CVpdfValidador -->
      <div class="mt-3">
        <button class="btn btn-outline-secondary btn-sm"
                @click="mostrarTexto = !mostrarTexto">
          <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">
            {{ mostrarTexto ? 'expand_less' : 'expand_more' }}
          </span>
          {{ mostrarTexto ? 'Ocultar texto PDF' : 'Ver texto completo del PDF' }}
        </button>
        <textarea v-if="mostrarTexto"
                  class="form-control form-control-sm mt-2"
                  rows="6" :value="resultado.textoCompleto" readonly />
      </div>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "Formulario002PdfValidador",

  // ── Props idénticos a CVpdfValidador ──────────────────────────────────────
  props: {
    idTramite: { type: Number, required: true },
    numero:    { type: String, required: true },
    autoValidar: { type: Boolean, default: false },
  },

  emits: ['resultado'],

  data() {
    return {
      resultado:    null,
      procesando:   false,
      errorMensaje: null,
      mostrarTexto: false,
    };
  },

  computed: {
    todoCoincide() {
      return this.resultado?.cedulaCoincide
          && this.resultado?.nombreCoincide
          && this.resultado?.fechaEnRango
          && !this.resultado?.diagnosticosNoPdf?.length
          && !this.resultado?.diagnosticosNoBd?.length;
    },

    alertaClase() {
      return this.todoCoincide ? 'alert-success' : 'alert-warning';
    },

    /** Set de CIEs de BD para resaltar filas coincidentes en la tabla de diagnósticos PDF */
    cieBdSet() {
      const diags = [
        this.resultado?.diag1Bd,
        this.resultado?.diag2Bd,
        this.resultado?.diag3Bd,
        this.resultado?.diag4Bd,
        this.resultado?.diag5Bd,
      ].filter(Boolean);
      return new Set(diags.map(d => d.toUpperCase()));
    },

    /** Tabla comparación — misma estructura exacta que CVpdfValidador */
    filasComparacion() {
      if (!this.resultado) return [];
      return [
        {
          campo:    'Cédula',
          bd:       this.resultado.cedulaBd,
          pdf:      this.resultado.cedulaPdf,
          coincide: this.resultado.cedulaCoincide,
        },
        {
          campo:    'Nombre',
          bd:       this.resultado.nombreBd,
          pdf:      this.resultado.nombrePdf,
          coincide: this.resultado.nombreCoincide,
        },
        {
          campo:    'Fecha Atención',
          bd:       `${this.resultado.fechaDesdeBd ?? '-'}  →  ${this.resultado.fechaHastaBd ?? '-'}`,
          pdf:      this.resultado.fechaAtencionPdf,
          coincide: this.resultado.fechaEnRango,
        },
        // ── Solo informativos ────────────────────────────────────────────────
        {
          campo:    'Institución',
          bd:       '-',
          pdf:      this.resultado.institucionPdf,
          coincide: null,
        },
        {
          campo:    'Establecimiento',
          bd:       '-',
          pdf:      this.resultado.establecimientoPdf,
          coincide: null,
        },
        {
          campo:    'No. HCU',
          bd:       '-',
          pdf:      this.resultado.numeroHcuPdf,
          coincide: null,
        },
        {
          campo:    'Motivo consulta',
          bd:       '-',
          pdf:      this.resultado.motivoConsultaPdf,
          coincide: null,
        },
      ];
    },
  },

  mounted() {
    if (this.autoValidar) this.validar002();
  },

  methods: {
    async ejecutarValidacion() {
      await this.validar002();
	},

    async validar002() {
      this.procesando   = true;
      this.resultado    = null;
      this.errorMensaje = null;
      this.mostrarTexto = false;

      try {
        const resp = await api.get(
          `/validacion-002/${this.idTramite}/${this.numero}`
        );
        const v = resp.data;

        if (!v.archivoEncontrado) {
          this.errorMensaje = v.mensajeError ?? "Archivo 002.pdf no encontrado.";
          this.$emit('resultado', 'sin-archivo');
          return;
        }

        this.resultado = v;
        this.$emit('resultado', this.calcularEstado());
      } catch (e) {
        console.error(e);
        this.$emit('resultado', 'error');		
        Swal.fire({
          icon:  "error",
          title: "Error",
          text:  "No se pudo validar el formulario 002.",
        });
      } finally {
        this.procesando = false;
      }
    },

calcularEstado() {
  if (!this.resultado) return null;
  if (!this.resultado.archivoEncontrado) return 'sin-archivo';

  // ── Errores críticos ───────────────────────────────────────
  const hayError =
    !this.resultado.cedulaCoincide ||
    !this.resultado.fechaEnRango;

  // ── Novedades leves ────────────────────────────────────────
  // diagnosticosNoPdf: están en BD pero no en el PDF → relevante
  // diagnosticosNoBd:  están en PDF pero no en BD   → informativo
  const hayLeve =
    this.resultado.diagnosticosNoPdf?.length > 0 ||
    this.resultado.diagnosticosNoBd?.length  > 0;

  // ── Nota: nombreCoincide se excluye porque el PDF del F002
  // extrae el nombre junto con los encabezados de columna,
  // lo que produce falsos negativos. Se muestra pero no afecta el estado.

  if (hayError) return 'error';
  if (hayLeve)  return 'leve';
  return 'ok';
},
  },
};
</script>